import { api } from './api'
import type {
  OpenPracticePositionRequest,
  PracticeAccess,
  PracticePosition,
  PracticePositionsResponse,
  PracticeTrade,
  PracticeWallet,
  TutorialProgress,
} from '../types/practice'

// Virtual margin practice simulator — 100% virtual money, real Kraken prices,
// never a real order. Admin-gated for now. Practice data is fully isolated
// from the signal record.
export const practiceAPI = {
  access: async () => (await api.get<PracticeAccess>('/api/practice/access')).data,

  wallet: async () => (await api.get<PracticeWallet>('/api/practice/wallet')).data,

  resetWallet: async () =>
    (await api.post<PracticeWallet>('/api/practice/wallet/reset')).data,

  // Open positions, priced live; also fires any pending liquidation/stop/target.
  positions: async () =>
    (await api.get<PracticePositionsResponse>('/api/practice/positions')).data,

  open: async (body: OpenPracticePositionRequest) =>
    (await api.post<PracticePosition>('/api/practice/positions', body)).data,

  // simulated_price is the tutorial's way of forcing an adverse move (step 4).
  refresh: async (id: number, simulated_price?: number) =>
    (
      await api.post<{
        closed: boolean
        outcome?: string
        trade?: PracticeTrade
        position?: PracticePosition
        simulated?: boolean
      }>(`/api/practice/positions/${id}/refresh`, simulated_price ? { simulated_price } : {})
    ).data,

  close: async (id: number, simulated_price?: number) =>
    (
      await api.post<{ closed: boolean; outcome: string; trade: PracticeTrade }>(
        `/api/practice/positions/${id}/close`,
        simulated_price ? { simulated_price } : {},
      )
    ).data,

  history: async () =>
    (await api.get<{ trades: PracticeTrade[]; note: string }>('/api/practice/history')).data,

  tutorial: async () =>
    (await api.get<TutorialProgress>('/api/practice/tutorial')).data,

  advanceTutorial: async (step: number) =>
    (await api.post<TutorialProgress>('/api/practice/tutorial/advance', { step })).data,

  resetTutorial: async () =>
    (await api.post<TutorialProgress>('/api/practice/tutorial/reset')).data,
}

// Extract a readable message from a practice refusal ({detail:{code,message}}).
export function practiceError(error: unknown, fallback = 'Something went wrong.'): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail
  if (typeof detail === 'string') return detail
  if (detail && typeof detail === 'object' && 'message' in detail) {
    return String((detail as { message: unknown }).message)
  }
  return fallback
}
